import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { brandColors } from '@/app/constants';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { CustomCard, CustomTooltip } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

const WINDOW_DAYS_DEFAULT = 14;
const WINDOW_DAYS_COMPACT = 7;

function activityTimeMs(activity: ActivityLog): number {
  const t = activity.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t) && t > 0) return t;
  if (activity.createdAt) {
    const ms = new Date(activity.createdAt).getTime();
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return 0;
}

function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface MemberActivityVolumeSectionProps {
  activities?: ActivityLog[];
  changeActiveStep: (step: string) => void;
  activitiesTabLabel: string;
  /** Narrow sidebar layout next to churn risk (fewer days, shorter chart). */
  compact?: boolean;
}

const MemberActivityVolumeSection: React.FC<MemberActivityVolumeSectionProps> = ({
  activities = [],
  changeActiveStep,
  activitiesTabLabel,
  compact = false,
}) => {
  const dayCount = compact ? WINDOW_DAYS_COMPACT : WINDOW_DAYS_DEFAULT;
  const chartMinHeightPx = compact ? 140 : 200;

  const chartData = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - (dayCount - 1));
    start.setHours(0, 0, 0, 0);
    const startMs = start.getTime();
    const endMs = end.getTime();

    const counts = new Map<string, number>();
    for (let i = 0; i < dayCount; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      counts.set(localDateKey(d), 0);
    }

    for (const log of activities) {
      const ms = activityTimeMs(log);
      if (ms < startMs || ms > endMs) continue;
      const key = localDateKey(new Date(ms));
      if (counts.has(key)) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }

    const rows: { label: string; count: number }[] = [];
    for (let i = 0; i < dayCount; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = localDateKey(d);
      rows.push({
        label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: counts.get(key) ?? 0,
      });
    }
    return rows;
  }, [activities, dayCount]);

  const totalInWindow = useMemo(() => chartData.reduce((s, r) => s + r.count, 0), [chartData]);

  return (
    <CustomCard
      title="Activity volume"
      className={
        compact ? 'flex h-full min-h-0 w-full min-w-0 flex-1 flex-col' : undefined
      }
    >
      <p className={`text-custom-darkSilver ${compact ? 'mb-2 text-xs' : 'mb-3 text-sm'}`}>
        {compact
          ? `Events per day · last ${dayCount} days`
          : `Events per day (last ${dayCount} days) from workspace logs matched to this member.`}
      </p>
      <div
        className="min-h-0 w-full min-w-0 flex-1"
        style={{ minHeight: chartMinHeightPx }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: compact ? -8 : 0, bottom: compact ? 4 : 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8e8ec" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: compact ? 8 : 10, fill: '#7085A8' }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e6' }}
              interval="preserveStartEnd"
              angle={compact ? -35 : 0}
              textAnchor={compact ? 'end' : 'middle'}
              height={compact ? 48 : undefined}
            />
            <YAxis
              width={compact ? 26 : 32}
              tick={{ fontSize: compact ? 9 : 10, fill: '#7085A8' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(112, 133, 168, 0.08)' }}
              content={<CustomTooltip />}
            />
            <Bar
              dataKey="count"
              name="Activities"
              fill={brandColors.blue}
              radius={[3, 3, 0, 0]}
              maxBarSize={compact ? 20 : 28}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div
        className={`flex flex-wrap items-center justify-between gap-2 text-custom-darkSilver ${
          compact ? 'mt-auto text-[11px]' : 'mt-3 text-xs'
        }`}
      >
        <span>{totalInWindow} total</span>
        <button
          type="button"
          onClick={() => changeActiveStep(activitiesTabLabel)}
          className="text-custom-blue font-semibold transition-colors border-b-2 border-white hover:border-custom-blue"
        >
          View all →
        </button>
      </div>
    </CustomCard>
  );
};

export default MemberActivityVolumeSection;
